
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import PhoneNumber from '@/common/components/UI/PhoneNumber/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import Geocoder from '@/modules/CreateOrderModule/components/Geocoder/index.vue'

// types
import { SparkTemplate } from '@/new-services/types/orders'

// masks
import { PHONE_NUMBER_MASK_INTERNATIONAL } from '@/core/constants/masks.constants'

// validations
import useVuelidate from '@vuelidate/core'
import { CreateTemplateSparkValidation } from '@/modules/CreateOrderModule/validations/CreateTemplateSpark.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CityT } from '@/core/types/cities.types'

// realisations
import ordersAPI from '@/new-services/orders.service'

// composable
import useHelpers from '@/common/composable/useHelpers'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        PhoneNumber,
        TheSelect,
        TheTextarea,
        TheButton,
        Geocoder,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<SparkTemplate>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const payload = reactive({
            cityId: null as number | null,
            fullAddress: null as string | null,
            phone: null as string | null,
            fullName: null as string | null,
            latitude: null as number | null,
            longitude: null as number | null,
            //
            street: null as string | null,
            comment: null as string | null,
            buildingNumber: null as string | null,
            flatNumber: null as string | null,
        })

        const state = reactive({
            city: null as CityT | null,
        })

        const geocode_state = reactive({
            is_opened: false,
            address: null as string | null,
            street: null as string | null,
            latitude: null as number | null,
            longitude: null as number | null,
        })

        const data_state = reactive({
            cities: new DataState<CityT>({}),
        })

        //

        const state_form$ = useVuelidate(CreateTemplateSparkValidation, payload)

        //

        const loadCities = async (page: number) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const params = {
                    page,
                    limit: 9999,
                }
                const response = await ordersAPI.getCities(params)
                data_state.cities.list = response.data.data
                data_state.cities.pagination.total_pages = response.data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        //

        const onCitySelect = async () => {
            if (!payload.cityId) return

            const { findObjectByValue } = useHelpers()
            const finded_city = findObjectByValue('id', payload.cityId, data_state.cities.list)

            if (!finded_city) return
            state.city = { ...finded_city }

            // resetting address on city change
            payload.fullAddress = null
            payload.latitude = null
            payload.longitude = null
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление отправного пункта'
            else if (props.isUpdate) return 'Редактирование отправного пункта'
            else if (props.isDelete) return 'Удаление отправного пункта'
            return 'Укажите адрес отправного пункта'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            else if (props.isUpdate) return 'Редактировать'
            else if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            emit('create', { ...payload })
        }

        const emitUpdate = () => {
            if (!props.initialValue) return
            emit('update', props.initialValue.id, { ...payload })
        }

        const emitManager = () => {
            if (geocode_state.is_opened) {
                if (!geocode_state.address || !geocode_state.latitude || !geocode_state.longitude) {
                    return
                }

                payload.fullAddress = geocode_state.address
                payload.street = geocode_state.street
                payload.latitude = geocode_state.latitude
                payload.longitude = geocode_state.longitude

                geocode_state.address = null
                geocode_state.street = null
                geocode_state.is_opened = false

                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
        }

        const onStartGeocode = () => {
            state_form$.value.$reset()

            state_form$.value.cityId.$touch()
            if (state_form$.value.cityId.$error) return

            geocode_state.is_opened = true

            geocode_state.address = payload.fullAddress
            geocode_state.street = payload.street
            geocode_state.latitude = payload.latitude
            geocode_state.longitude = payload.longitude
        }

        const onCancelGeocode = () => {
            geocode_state.is_opened = false
            geocode_state.address = null
            geocode_state.street = null
            geocode_state.latitude = null
            geocode_state.longitude = null
        }

        const onSetAddress = ({
            address,
            street,
            latitude,
            longitude,
        }: {
            address: string
            street: string | null
            latitude: number
            longitude: number
        }) => {
            geocode_state.address = address
            geocode_state.street = street
            geocode_state.latitude = latitude
            geocode_state.longitude = longitude
        }

        const emitCancel = () => {
            if (geocode_state.is_opened) {
                onCancelGeocode()
                return
            }

            emit('cancel')
        }

        const isAddressDisabled = computed(() => {
            return Boolean(!state.city)
        })

        const fillInfo = async () => {
            if (props.initialValue) {
                payload.cityId = props.initialValue.cityId
                payload.fullAddress = props.initialValue.fullAddress
                payload.phone = props.initialValue.phoneNumber
                payload.fullName = props.initialValue.fullName
                payload.latitude = Number(props.initialValue.latitude)
                payload.longitude = Number(props.initialValue.longitude)

                payload.street = props.initialValue.street
                payload.comment = props.initialValue.comment
                payload.buildingNumber = props.initialValue.buildingNumber
                payload.flatNumber = props.initialValue.flatNumber

                const { findObjectByValue } = useHelpers()
                const finded_city = findObjectByValue('id', payload.cityId, data_state.cities.list)
                if (!finded_city) return

                state.city = { ...finded_city }
                return
            }
        }

        //

        onBeforeMount(async () => {
            await loadCities(1)
            fillInfo()
        })

        return {
            payload,
            state,
            geocode_state,
            //
            data_state,
            //
            state_form$,
            //
            loadCities,
            onCitySelect,
            //
            emitManager,
            emitCancel,
            //
            onStartGeocode,
            onCancelGeocode,
            onSetAddress,
            //
            PHONE_NUMBER_MASK_INTERNATIONAL,
            //
            isAddressDisabled,
            modalTitle,
            modalConfirmButtonText,
        }
    },
})
